import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

// Create a singleton instance to avoid re-initialization
const mdParser = new MarkdownIt({
  html: true, // Allow HTML tags in Markdown
  breaks: true, // Convert newlines to <br>
  linkify: true, // Autoconvert URLs to links
});

// Cache for rendered markdown to avoid re-parsing identical content
const markdownCache = new Map<string, string>();
const CACHE_SIZE_LIMIT = 100; // Prevent memory leaks

/**
 * Converts markdown to HTML and highlights the search term efficiently.
 * Uses caching and optimized string replacement for better performance.
 */
export function markdownToHtmlWithHighlight(md: string, searchTerm: string = ''): string {
  if (!md) return '';

  // Create cache key
  const cacheKey = `${md}|${searchTerm}`;
  
  // Check cache first
  if (markdownCache.has(cacheKey)) {
    return markdownCache.get(cacheKey)!;
  }

  // Render markdown to HTML
  const html = mdParser.render(md);
  
  let result: string;
  
  if (!searchTerm?.trim()) {
    result = DOMPurify.sanitize(addProperSpacing(html));
  } else {
    // Use efficient string-based highlighting instead of DOM manipulation
    result = highlightSearchTerm(html, searchTerm.trim());
  }

  // Cache management - prevent memory leaks
  if (markdownCache.size >= CACHE_SIZE_LIMIT) {
    const firstKey = markdownCache.keys().next().value;
    if (firstKey !== undefined) {
      markdownCache.delete(firstKey);
    }
  }
  
  markdownCache.set(cacheKey, result);
  return result;
}

/**
 * Adds proper spacing and formatting to HTML content for better display
 */
function addProperSpacing(html: string): string {
  return html
    // Add proper spacing after paragraphs
    .replace(/<\/p>/g, '</p><div style="margin-bottom: 16px;"></div>')
    // Add proper spacing after headings
    .replace(/<\/(h[1-6])>/g, '</$1><div style="margin-bottom: 12px;"></div>')
    // Add proper spacing after list items
    .replace(/<\/li>/g, '</li><div style="margin-bottom: 8px;"></div>')
    // Add proper spacing after lists
    .replace(/<\/(ul|ol)>/g, '</$1><div style="margin-bottom: 16px;"></div>')
    // Add text justification and line height to paragraphs
    .replace(/<p>/g, '<p style="text-align: justify; line-height: 1.6; margin-bottom: 12px; word-spacing: 0.1em;">')
    // Add proper spacing for div elements
    .replace(/<div>/g, '<div style="margin-bottom: 8px;">')
    // Ensure proper word spacing in all text content
    .replace(/(\w)\s+(\w)/g, '$1 $2');
}

/**
 * Efficiently highlights search terms using string replacement
 * that avoids highlighting text inside HTML tags and adds proper spacing.
 */
function highlightSearchTerm(html: string, searchTerm: string): string {
  // First, add proper spacing and formatting to the HTML
  const formattedHtml = addProperSpacing(html);
  
  // Escape special regex characters
  const escaped = searchTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
  
  // Split HTML into text and tag parts
  const parts: Array<{ type: 'text' | 'tag'; content: string }> = [];
  let lastIndex = 0;
  
  // Regex to match HTML tags
  const tagRegex = /<[^>]*>/g;
  let tagMatch;
  
  while ((tagMatch = tagRegex.exec(formattedHtml)) !== null) {
    // Add text before the tag
    if (tagMatch.index > lastIndex) {
      parts.push({ type: 'text', content: formattedHtml.slice(lastIndex, tagMatch.index) });
    }
    
    // Add the tag
    parts.push({ type: 'tag', content: tagMatch[0] });
    lastIndex = tagMatch.index + tagMatch[0].length;
  }
  
  // Add remaining text after the last tag
  if (lastIndex < formattedHtml.length) {
    parts.push({ type: 'text', content: formattedHtml.slice(lastIndex) });
  }
  
  // Create regex for partial matching (including single letters)
  const regex = new RegExp(`(${escaped})`, 'gi');
  
  // Highlight only in text parts, not in HTML tags
  const highlighted = parts.map((part) => {
    if (part.type === 'text') {
      return part.content.replace(regex, (match: string) => 
        `<span class="inline bg-BeeMG-yellow font-bold" style="font-family: 'Sora', sans-serif;">${match}</span>`
      );
    }
    return part.content;
  }).join('');
  
  return DOMPurify.sanitize(highlighted, {
    ADD_ATTR: ['style'] // Allow style attributes for proper spacing
  });
}

/**
 * Clear the markdown cache when needed (e.g., on app restart or memory pressure)
 */
export function clearMarkdownCache(): void {
  markdownCache.clear();
}